import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Avatar,
  Switch,
  CircularProgress,
  IconButton,
  Chip
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SecurityIcon from '@mui/icons-material/Security';
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { startLoading, stopLoading, setAlertData } from "../../Redux/Screen/Action";
import AdminApi from "Service/Admin.api";
import OdrApi from "Service/Odr.api";
import { StyledTableCell, StyledTableRow } from "./ProfessionalUserList";

const ManageOdrRoles: React.FC = () => {
  const [professionals, setProfessionals] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingUserRole, setUpdatingUserRole] = useState<{ [key: string]: boolean }>({});
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    fetchProfessionals();
  }, []);

  const fetchProfessionals = async () => {
    dispatch(startLoading({ loading: true, loadtext: "Fetching verified professionals..." }));
    try {
      const response = await AdminApi.GetAllActiveProfessionalUser();
      if (response?.success && Array.isArray(response.data)) {
        setProfessionals(response.data);
      } else {
        setProfessionals([]);
      }
    } catch (error) {
      console.error(error);
      dispatch(
        setAlertData({
          alertMessage: "Failed to load professionals.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      dispatch(stopLoading());
    }
  };

  const handleToggleRole = async (profId: number, role: string, hasRole: boolean) => {
    const actionKey = `${profId}-${role}`;
    setUpdatingUserRole((prev) => ({ ...prev, [actionKey]: true }));

    try {
      let response;
      if (hasRole) {
        // Remove role
        response = await OdrApi.removeOdrRole(profId, role);
      } else {
        // Assign role
        response = await OdrApi.assignOdrRole(profId, role);
      }

      if (response?.success) {
        dispatch(
          setAlertData({
            alertMessage: response.message || `Role updated successfully.`,
            alertVariant: "success",
            openAlert: true,
          })
        );
        // Update local state
        setProfessionals((prevProfs) =>
          prevProfs.map((prof) => {
            if (prof.id === profId) {
              const currentRoles = prof.roles || [];
              const updatedRoles = hasRole
                ? currentRoles.filter((r: string) => r.toLowerCase() !== role.toLowerCase())
                : [...currentRoles, role];
              return { ...prof, roles: updatedRoles };
            }
            return prof;
          })
        );
      } else {
        dispatch(
          setAlertData({
            alertMessage: response?.message || "Failed to update role.",
            alertVariant: "error",
            openAlert: true,
          })
        );
      }
    } catch (error: any) {
      console.error(error);
      dispatch(
        setAlertData({
          alertMessage: error?.message || "Error modifying ODR role.",
          alertVariant: "error",
          openAlert: true,
        })
      );
    } finally {
      setUpdatingUserRole((prev) => ({ ...prev, [actionKey]: false }));
    }
  };

  const filteredProfs = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return professionals;

    return professionals.filter((p) => {
      const firstName = p.firstName?.toLowerCase() || "";
      const lastName = p.lastName?.toLowerCase() || "";
      const fullName = `${firstName} ${lastName}`;
      const email = p.email?.toLowerCase() || "";
      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        fullName.includes(query) ||
        email.includes(query)
      );
    });
  }, [searchQuery, professionals]);

  return (
    <Box p={{ xs: 2, sm: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ fontWeight: 'bold', textTransform: 'none' }}
        >
          Back
        </Button>
      </Box>

      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
        <SecurityIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography
          variant="h5"
          fontWeight="bold"
          sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}
        >
          Manage ODR Role Privileges
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Assign or revoke ODR-specific role privileges (Arbitrator, Mediator, Negotiator) for verified professional users.
      </Typography>

      <TextField
        fullWidth
        placeholder="Search by name or email..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{
          mb: 3,
          maxWidth: { xs: '100%', sm: 400 }
        }}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: 'gray' }} />
        }}
        size="small"
      />

      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: 3,
          maxHeight: { xs: '60vh', sm: '70vh' },
          overflow: "auto"
        }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <StyledTableCell>Professional</StyledTableCell>
              <StyledTableCell>Contact Info</StyledTableCell>
              <StyledTableCell align="center">Arbitrator</StyledTableCell>
              <StyledTableCell align="center">Mediator</StyledTableCell>
              <StyledTableCell align="center">Negotiator</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredProfs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                  <Typography color="text.primary">No verified professionals found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredProfs.map((prof) => {
                const rolesList = prof.roles || [];
                const isArbitrator = rolesList.some((r: string) => r.toLowerCase() === "arbitrator");
                const isMediator = rolesList.some((r: string) => r.toLowerCase() === "mediator");
                const isNegotiator = rolesList.some((r: string) => r.toLowerCase() === "negotiator");

                return (
                  <StyledTableRow key={prof.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
                        <Avatar sx={{ bgcolor: '#283165', width: 36, height: 36, fontSize: '1rem' }}>
                          {prof.firstName ? prof.firstName[0].toUpperCase() : 'P'}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {prof.firstName} {prof.lastName}
                          </Typography>
                          <Typography variant="caption" color="text.primary">
                            ID: {prof.id}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.875rem' }}>
                      <Typography variant="body2">{prof.email}</Typography>
                      <Typography variant="caption" color="text.primary">{prof.contactNumber}</Typography>
                    </TableCell>

                    {/* Arbitrator Toggle */}
                    <TableCell align="center">
                      {updatingUserRole[`${prof.id}-Arbitrator`] ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Switch
                          checked={isArbitrator}
                          onChange={() => handleToggleRole(prof.id, "Arbitrator", isArbitrator)}
                          color="primary"
                        />
                      )}
                    </TableCell>

                    {/* Mediator Toggle */}
                    <TableCell align="center">
                      {updatingUserRole[`${prof.id}-Mediator`] ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Switch
                          checked={isMediator}
                          onChange={() => handleToggleRole(prof.id, "Mediator", isMediator)}
                          color="primary"
                        />
                      )}
                    </TableCell>

                    {/* Negotiator Toggle */}
                    <TableCell align="center">
                      {updatingUserRole[`${prof.id}-Negotiator`] ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Switch
                          checked={isNegotiator}
                          onChange={() => handleToggleRole(prof.id, "Negotiator", isNegotiator)}
                          color="primary"
                        />
                      )}
                    </TableCell>
                  </StyledTableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ManageOdrRoles;
